import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getImageUrl } from '../utils/apiConfig';
import { useAuth } from '../context/UnifiedAuthContext';
import Swal from 'sweetalert2';

const AccountSettingsModal = ({ isOpen, onClose }) => {
  const { checkAuthStatus } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    loginEmail: '',        // Read-only login credential
    notificationEmail: '', // Editable notification email
    phone: '',
    profilePicture: '',
    liquorOfChoice: '',
    emailNotificationsEnabled: true,
    fantasyResetNotifications: true,
    pickReminderNotifications: true
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadProfile = useCallback(async () => {
    if (!isOpen) return;

    try {
      console.log('[AccountSettings] Loading profile from:', `${API_BASE_URL}/auth/profile/full`);
      const response = await fetch(`${API_BASE_URL}/auth/profile/full`, {
        credentials: 'include'
      });
      const result = await response.json();
      console.log('[AccountSettings] Response:', response.status, result);

      if (response.ok && result.data) {
        const profileData = result.data.profile;
        console.log('[AccountSettings] Profile data:', profileData);
        setProfile({
          name: profileData.name || '',
          loginEmail: profileData.loginEmail || '',           // Read-only login credential
          notificationEmail: profileData.notificationEmail || '', // Editable notification email
          phone: profileData.phone || '',
          profilePicture: profileData.image_path || '',
          liquorOfChoice: profileData.liquor_of_choice || '',
          emailNotificationsEnabled: profileData.email_notifications_enabled !== false,
          fantasyResetNotifications: profileData.fantasy_reset_notifications !== false,
          pickReminderNotifications: profileData.pick_reminder_notifications !== false
        });
        // Set the current profile picture as preview if it exists
        if (profileData.image_path) {
          setProfilePicturePreview(getImageUrl(profileData.image_path));
        }
      } else {
        console.error('Failed to load profile:', result.error);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  }, [isOpen]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Reset message when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMessage({ type: '', text: '' });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // First, handle profile picture upload if there's a new file
      let profilePicturePath = profile.profilePicture;
      
      if (profilePictureFile) {
        const formData = new FormData();
        formData.append('profilePicture', profilePictureFile);
        
        const uploadResponse = await fetch(`${API_BASE_URL}/auth/profile/picture`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          profilePicturePath = uploadResult.imagePath;
        } else {
          const uploadError = await uploadResponse.json();
          throw new Error(uploadError.error || 'Failed to upload profile picture');
        }
      }

      // Then update the profile data
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: profile.name,
          notificationEmail: profile.notificationEmail,
          phone: profile.phone,
          liquorOfChoice: profile.liquorOfChoice,
          emailNotificationsEnabled: profile.emailNotificationsEnabled,
          fantasyResetNotifications: profile.fantasyResetNotifications,
          pickReminderNotifications: profile.pickReminderNotifications
        })
      });

      const result = await response.json();

      if (response.ok && result.data) {
        const profileData = result.data.profile;
        // Update the profile state with the response data
        setProfile(prev => ({
          ...prev,
          name: profileData.name || '',
          loginEmail: profileData.loginEmail || prev.loginEmail, // Keep existing login email
          notificationEmail: profileData.notificationEmail || '',
          phone: profileData.phone || '',
          profilePicture: profileData.image_path || profilePicturePath,
          liquorOfChoice: profileData.liquor_of_choice || '',
          emailNotificationsEnabled: profileData.email_notifications_enabled !== false,
          fantasyResetNotifications: profileData.fantasy_reset_notifications !== false,
          pickReminderNotifications: profileData.pick_reminder_notifications !== false
        }));

        // Clear the file input and update preview
        setProfilePictureFile(null);
        if (profileData.image_path || profilePicturePath) {
          const imagePath = profileData.image_path || profilePicturePath;
          setProfilePicturePreview(getImageUrl(imagePath));
        }

        // Refresh auth context to update user data across the app
        await checkAuthStatus();
        
        // Show success notification and close modal
        Swal.fire({
          icon: 'success',
          title: 'Profile Updated!',
          text: 'Your profile has been successfully updated.',
          confirmButtonText: 'Great!',
        }).then(() => {
          onClose();
        });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image file size must be less than 5MB' });
        return;
      }

      setProfilePictureFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous messages
      setMessage({ type: '', text: '' });
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Account Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {message.text && (
            <div className={`mb-4 p-3 rounded ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'bg-red-100 text-red-700 border border-red-300'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Profile Picture Upload */}
            <div>
              <label className="block text-sm font-medium mb-1">Profile Picture</label>
              <div className="flex items-start space-x-4">
                {/* Current/Preview Image */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full border-2 border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center">
                    {profilePicturePreview ? (
                      <img 
                        src={profilePicturePreview} 
                        alt="Profile preview" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Upload Controls */}
                <div className="flex-1">
                  <input
                    type="file"
                    id="profilePictureInput"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="profilePictureInput"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Choose Image
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Full Name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Login Email</label>
              <input
                type="email"
                value={profile.loginEmail}
                className="w-full p-2 border rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                disabled
                readOnly
              />
              <p className="text-sm text-gray-500 mt-1">This email is used for login and cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email for Notifications</label>
              <input
                type="email"
                value={profile.notificationEmail}
                onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-sm text-gray-500 mt-1">We'll send fantasy golf updates and other notifications to this email</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Liquor of Choice</label>
              <input
                type="text"
                value={profile.liquorOfChoice}
                onChange={(e) => handleInputChange('liquorOfChoice', e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Bourbon, Tequila, Rum..."
              />
            </div>

            {/* Notification Preferences Section */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Email Notification Preferences</h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="emailNotificationsEnabled"
                    checked={profile.emailNotificationsEnabled}
                    onChange={(e) => handleInputChange('emailNotificationsEnabled', e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="emailNotificationsEnabled" className="ml-2 block text-sm text-gray-900">
                    Enable email notifications
                  </label>
                </div>

                <div className="flex items-center ml-6">
                  <input
                    type="checkbox"
                    id="fantasyResetNotifications"
                    checked={profile.fantasyResetNotifications}
                    onChange={(e) => handleInputChange('fantasyResetNotifications', e.target.checked)}
                    disabled={!profile.emailNotificationsEnabled}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="fantasyResetNotifications" className="ml-2 block text-sm text-gray-700">
                    Fantasy pick reset notifications
                  </label>
                </div>

                <div className="flex items-center ml-6">
                  <input
                    type="checkbox"
                    id="pickReminderNotifications"
                    checked={profile.pickReminderNotifications}
                    onChange={(e) => handleInputChange('pickReminderNotifications', e.target.checked)}
                    disabled={!profile.emailNotificationsEnabled}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <label htmlFor="pickReminderNotifications" className="ml-2 block text-sm text-gray-700">
                    Pick reminder notifications
                  </label>
                </div>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                You can opt out of specific types of notifications while keeping your account active.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
